import { brl } from '../../lib/format'
import { withBase } from '../../lib/basePath'
import { color, radius } from '../../theme/tokens'
import type { AudioSource, Briefing as BriefingData, RealtimeStatus, Scenario } from '../../lib/types'
import { Check } from '../ui'

const SOURCE_NOTES: Record<AudioSource, string> = {
  sim: 'Roteiro versionado, sem consumir API. O orb reage a uma amplitude sintética.',
  mic: 'Roteiro versionado, mas o orb reage ao microfone da sala. Fallback automático se a permissão for negada.',
  rtc: 'Conversa real — iniciada na tela de chamada (Modelo A).',
  pipeline: 'Conversa real — iniciada na tela de chamada.',
  wa: 'Conversa real por texto — iniciada na tela do WhatsApp.',
}

/** Briefing pré-chamada: o que o operador precisa saber antes de discar. */
export function Briefing({
  data,
  scenarios,
  source,
  scenarioId,
  realtime,
  starting,
  linePeers,
  onSourceChange,
  onScenarioChange,
  onStart,
}: {
  data: BriefingData
  scenarios: Scenario[]
  source: AudioSource
  scenarioId: string
  realtime: RealtimeStatus | null
  starting: boolean
  /** Quantas abas `/linha` estão abertas para atender. */
  linePeers: number
  onSourceChange(next: AudioSource): void
  onScenarioChange(next: string): void
  onStart(): void
}) {
  const rtcDisabled = realtime ? !realtime.enabled : true

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
      <div
        style={{
          flex: 1,
          background: color.panel,
          borderRadius: radius.panel,
          padding: '36px 40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 24,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(60% 80% at 78% 20%, rgba(240,134,47,.14), transparent 70%)',
          }}
        />

        <div style={{ position: 'relative' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.1em',
              color: color.brand,
              marginBottom: 10,
            }}
          >
            BRIEFING PRÉ-CHAMADA
          </div>
          <h1
            style={{ fontSize: 32, fontWeight: 600, margin: '0 0 6px', letterSpacing: '-.5px' }}
          >
            {data.case.name}
          </h1>
          <div style={{ fontSize: 14, color: color.panelTextSoft }}>
            {data.case.segment}
            {data.case.vip ? ' · VIP' : ''} · {data.case.days_overdue} dias em atraso · linha{' '}
            {data.case.phone}
          </div>
        </div>

        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 14,
          }}
        >
          <BriefCard title="OBJETIVO DA CHAMADA">
            <div style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {brl(data.objective_value)}
            </div>
            <div style={{ fontSize: 12, color: color.panelText, marginTop: 4 }}>
              {data.objective_note}
            </div>
          </BriefCard>

          <BriefCard
            title={data.negotiation_blocked ? 'NEGOCIAÇÃO BLOQUEADA' : 'POLÍTICA A SER APLICADA'}
            border={data.negotiation_blocked ? 'rgba(237,79,46,.45)' : undefined}
            background={data.negotiation_blocked ? 'rgba(237,79,46,.12)' : undefined}
            titleColor={data.negotiation_blocked ? '#ffb3a7' : undefined}
          >
            <div style={{ fontSize: 14, fontWeight: 600 }}>{data.applied_line}</div>
            <div
              style={{
                fontSize: 12,
                color: data.negotiation_blocked ? '#ffd9cf' : color.panelText,
                marginTop: 4,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {data.caps_label}
            </div>
            <div style={{ fontSize: 11, color: color.panelTextDim, marginTop: 6 }}>
              Política v{data.policy_version} · publicada {data.policy_published_at}
            </div>
          </BriefCard>

          <BriefCard
            title="JANELA DE CONTATO"
            border="rgba(30,158,99,.4)"
            background="rgba(30,158,99,.1)"
            titleColor={color.greenPanel}
          >
            <div
              style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
              {data.best_window} · melhor faixa
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: '#c9ecd9',
                marginTop: 4,
              }}
            >
              {data.within_window ? (
                <>
                  <span style={{ color: '#5fd39a', display: 'flex' }}>
                    <Check size={14} />
                  </span>
                  Dentro da janela permitida
                </>
              ) : (
                'Fora da janela — a régua não dispara agora'
              )}
            </div>
          </BriefCard>
        </div>

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <a
            href={withBase("linha")}
            target="_blank"
            rel="noreferrer"
            style={{
              background: color.green,
              color: '#fff',
              border: 'none',
              borderRadius: radius.lg,
              padding: '15px 26px',
              fontSize: 15,
              fontWeight: 700,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              boxShadow: '0 6px 18px rgba(30,158,99,.3)',
              opacity: rtcDisabled ? 0.55 : 1,
              pointerEvents: rtcDisabled ? 'none' : undefined,
            }}
            title={
              rtcDisabled
                ? 'Configure OPENAI_API_KEY e reinicie o backend'
                : 'Abre a tela de chamada em outra aba'
            }
          >
            <span aria-hidden>✆</span>
            {rtcDisabled ? 'Chamada real (sem chave)' : 'Abrir a chamada real'}
          </a>

          <button
            type="button"
            onClick={onStart}
            disabled={starting}
            style={{
              background: 'transparent',
              color: color.panelText,
              border: `1px solid ${color.panelBorder}`,
              borderRadius: radius.lg,
              padding: '15px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: starting ? 'wait' : 'pointer',
              opacity: starting ? 0.6 : 1,
            }}
          >
            {starting ? 'Iniciando…' : 'Rodar roteiro simulado'}
          </button>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11.5, color: color.panelTextMuted, fontWeight: 600 }}>
              Áudio do roteiro
            </span>
            <select
              value={source === 'rtc' ? 'sim' : source}
              onChange={(event) => onSourceChange(event.target.value as AudioSource)}
              style={selectStyle}
            >
              <option value="sim">Simulada (sem microfone)</option>
              <option value="mic">Microfone da sala</option>
            </select>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11.5, color: color.panelTextMuted, fontWeight: 600 }}>
              Cenário
            </span>
            <select
              value={scenarioId}
              onChange={(event) => onScenarioChange(event.target.value)}
              style={selectStyle}
            >
              {scenarios.map((scenario) => (
                <option key={scenario.id} value={scenario.id}>
                  {scenario.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div
          style={{
            position: 'relative',
            fontSize: 11.5,
            color: color.panelTextDim,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <span>
            A conversa real acontece na tela de chamada; este console a espelha
            automaticamente assim que ela começa.
            {realtime?.enabled ? ` Modelo ${realtime.model} · voz ${realtime.voice}.` : ''}
            {realtime && !realtime.enabled ? ` ${realtime.reason}.` : ''}
          </span>
          <span>Roteiro simulado: {SOURCE_NOTES[source === 'rtc' ? 'sim' : source]}</span>

          {linePeers > 0 ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                alignSelf: 'flex-start',
                fontSize: 11.5,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: radius.pill,
                background: 'rgba(30,158,99,.14)',
                border: '1px solid rgba(30,158,99,.4)',
                color: color.greenPanel,
              }}
            >
              <span
                style={{ width: 7, height: 7, borderRadius: '50%', background: color.green }}
              />
              Tela de chamada aberta ({linePeers}) · pronta para iniciar
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const selectStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: '8px 10px',
  borderRadius: radius.md,
  border: `1px solid ${color.panelBorder}`,
  background: color.panelRaised,
  color: '#fff',
}

function BriefCard({
  title,
  children,
  border = 'rgba(255,255,255,.14)',
  background = 'rgba(255,255,255,.04)',
  titleColor = color.panelTextMuted,
}: {
  title: string
  children: React.ReactNode
  border?: string
  background?: string
  titleColor?: string
}) {
  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: radius.xl,
        padding: '16px 18px',
        background,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.06em',
          color: titleColor,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  )
}
